import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Link2, Unlink, AlertCircle, Key, Copy, Eye, EyeOff, FileText, Zap, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function IntegrationsPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/google/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Surface success/error from OAuth callback (?gcal_ok=1 or ?gcal_error=…)
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal_ok")) {
      setNotice("Google Calendar connected. Future bookings will land on your calendar.");
      params.delete("gcal_ok"); params.delete("tab");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`);
    }
    const err = params.get("gcal_error");
    if (err) {
      setError(`Google Calendar connection failed: ${err}`);
      params.delete("gcal_error"); params.delete("tab");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`);
    }
  }, []);

  const connect = async () => {
    setBusy(true); setError(""); setNotice(null);
    try {
      const { data } = await api.post("/integrations/google/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      setError(formatApiError(e));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Future bookings won't be added to your calendar.")) return;
    setBusy(true); setError(""); setNotice(null);
    try {
      await api.post("/integrations/google/disconnect");
      setNotice("Google Calendar disconnected.");
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-[var(--muted)] text-sm">Loading integrations…</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="integrations-panel">
      <div>
        <h3 className="font-display text-2xl text-[var(--navy)]">Integrations</h3>
        <p className="text-sm text-[var(--muted)] mt-1">
          Connect external services to automate your workflow.
        </p>
      </div>

      {notice && (
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 text-green-800 text-sm p-3.5" data-testid="integrations-notice">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-800 text-sm p-3.5" data-testid="integrations-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="border border-[var(--line)] bg-white p-6 flex items-start gap-4">
        <div className="w-11 h-11 flex-shrink-0 bg-[var(--cream)] border border-[var(--line)] flex items-center justify-center">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path fill="#4285F4" d="M22 5.7c0-.9-.7-1.7-1.7-1.7H18V2h-2v2H8V2H6v2H3.7C2.7 4 2 4.8 2 5.7v14.6C2 21.2 2.7 22 3.7 22h16.6c1 0 1.7-.8 1.7-1.7V5.7z"/>
            <path fill="#fff" d="M20 8H4v12h16z"/>
            <text x="12" y="17" text-anchor="middle" font-family="Arial" font-size="7" font-weight="bold" fill="#4285F4">31</text>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-[var(--navy)]">Google Calendar</h4>
            {status?.connected && (
              <span className="text-[10px] tracking-widest bg-green-100 text-green-800 px-2 py-0.5 font-bold">CONNECTED</span>
            )}
          </div>
          <p className="text-sm text-[var(--muted)] mt-1">
            Auto-create an event on your calendar for every new consultation booking, with the prospect invited as an attendee.
          </p>
          {status?.connected && status?.connected_email && (
            <p className="text-xs text-[var(--muted)] mt-2">Connected as <strong className="text-[var(--navy)]">{status.connected_email}</strong></p>
          )}
          {!status?.configured && (
            <p className="text-xs text-red-700 mt-2">Server not configured — set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET.</p>
          )}
          <div className="mt-4">
            {status?.connected ? (
              <button
                onClick={disconnect} disabled={busy}
                className="btn btn-ghost !py-2 !px-4 !text-[11px] disabled:opacity-60"
                data-testid="gcal-disconnect"
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={12} /> : <Unlink size={12} className="mr-2" />} Disconnect
              </button>
            ) : (
              <button
                onClick={connect} disabled={busy || !status?.configured}
                className="btn btn-navy !py-2 !px-4 !text-[11px] disabled:opacity-60"
                data-testid="gcal-connect"
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={12} /> : <Link2 size={12} className="mr-2" />} Connect Google Calendar
              </button>
            )}
          </div>
        </div>
      </div>

      <MakeWebhookCard />

      <BlogApiKeyCard />

      <p className="text-xs text-[var(--muted)] pt-2 leading-relaxed">
        More integrations coming soon — Slack notifications, HubSpot lead sync, Zapier webhooks.
      </p>
    </div>
  );
}

function BlogApiKeyCard() {
  const [state, setState] = useState({ loading: true, configured: false, key: null });
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/blog-api-key");
        setState({ loading: false, ...data });
      } catch (e) {
        setState({ loading: false, configured: false, key: null, error: formatApiError(e) });
      }
    })();
  }, []);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const endpoint = `${backend}${state.endpoint || "/api/blog/publish"}`;
  const displayKey = state.key
    ? (reveal ? state.key : `${state.key.slice(0, 4)}${"•".repeat(Math.max(0, state.key.length - 8))}${state.key.slice(-4)}`)
    : "—";

  const curlExample = state.key ? `curl -X POST ${endpoint} \\
  -H "Authorization: Bearer ${reveal ? state.key : "$BLOG_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "My Post Title",
    "slug": "my-post-title",
    "excerpt": "One-sentence teaser for listings and SEO.",
    "body_md": "# Heading\\n\\nMarkdown body with **bold**, links, images…",
    "category": "Insights",
    "tags": ["catalyst", "hr"],
    "author": "Jimmy Mack",
    "status": "published",
    "upsert_by_slug": true
  }'` : "";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1200);
    });
  };

  return (
    <div className="border border-[var(--line)] bg-white" data-testid="blog-api-key-card">
      <div className="px-5 py-4 border-b border-[var(--line)] flex items-center gap-3">
        <FileText size={16} className="text-[var(--gold)]" />
        <div className="flex-1">
          <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Blog Publish API Key</div>
          <div className="text-xs text-[var(--muted)]">Publish or upsert blog posts from external tools (Zapier, N8N, GitHub Actions, cron scripts).</div>
        </div>
        {state.loading ? <Loader2 className="animate-spin" size={14} /> : state.configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 uppercase tracking-widest">
            <CheckCircle2 size={12} /> Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 uppercase tracking-widest">
            <AlertCircle size={12} /> Not Configured
          </span>
        )}
      </div>

      {!state.loading && !state.configured && (
        <div className="p-5 text-xs text-[var(--muted)]">
          Set the <code className="bg-[var(--cream)] px-1">BLOG_API_KEY</code> environment variable on your backend to enable this endpoint.
        </div>
      )}

      {!state.loading && state.configured && (
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-1.5">
              <Key size={11} className="inline mr-1 -mt-0.5" /> API Key
            </label>
            <div className="flex gap-2">
              <input readOnly value={displayKey} className="flex-1 px-3 py-2 border border-[var(--line)] bg-[var(--cream)] font-mono text-xs" data-testid="blog-api-key-value" />
              <button type="button" onClick={() => setReveal((r) => !r)} className="px-3 py-2 border border-[var(--line)] hover:bg-[var(--cream)]" title={reveal ? "Hide" : "Reveal"} data-testid="blog-api-key-reveal">
                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button type="button" onClick={() => copy(state.key, "key")} className="px-3 py-2 border border-[var(--line)] hover:bg-[var(--cream)] text-xs font-bold uppercase tracking-widest" data-testid="blog-api-key-copy">
                {copied === "key" ? "Copied" : <><Copy size={12} className="inline mr-1" /> Copy</>}
              </button>
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-1.5">
              Keep this key secret. To rotate, update <code className="bg-[var(--cream)] px-1">BLOG_API_KEY</code> in your backend env and redeploy.
            </p>
          </div>

          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-1.5">Endpoint</label>
            <div className="flex gap-2">
              <input readOnly value={endpoint} className="flex-1 px-3 py-2 border border-[var(--line)] bg-[var(--cream)] font-mono text-xs" data-testid="blog-api-key-endpoint" />
              <button type="button" onClick={() => copy(endpoint, "endpoint")} className="px-3 py-2 border border-[var(--line)] hover:bg-[var(--cream)] text-xs font-bold uppercase tracking-widest">
                {copied === "endpoint" ? "Copied" : <><Copy size={12} className="inline mr-1" /> Copy</>}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] mb-1.5">Example Request</label>
            <pre className="text-[11px] bg-[var(--navy)] text-neutral-100 p-3 overflow-x-auto whitespace-pre font-mono leading-relaxed" data-testid="blog-api-key-curl">{curlExample}</pre>
            <div className="flex justify-end mt-1.5">
              <button type="button" onClick={() => copy(curlExample, "curl")} className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)] hover:text-[var(--gold)]">
                {copied === "curl" ? "Copied ✓" : <><Copy size={11} className="inline mr-1" /> Copy example</>}
              </button>
            </div>
          </div>

          <details className="text-xs text-[var(--muted)]">
            <summary className="cursor-pointer text-[var(--navy)] font-bold text-[11px] tracking-widest uppercase">Field reference</summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc leading-relaxed">
              <li><code>title</code> — required</li>
              <li><code>body_md</code> — required, Markdown supported</li>
              <li><code>slug</code> — optional; auto-derived from title if omitted</li>
              <li><code>excerpt</code>, <code>author</code>, <code>hero_image_url</code>, <code>tags[]</code>, <code>seo_title</code>, <code>seo_description</code> — optional</li>
              <li><code>category</code> — one of the allowed values; defaults to <code>Insights</code></li>
              <li><code>status</code> — <code>draft</code> or <code>published</code> (defaults to <code>published</code> for API key)</li>
              <li><code>upsert_by_slug</code> — if <code>true</code>, updates an existing post with the same slug</li>
              <li>Auth: <code>Authorization: Bearer &lt;key&gt;</code> or <code>X-API-Key: &lt;key&gt;</code></li>
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}

function MakeWebhookCard() {
  const [state, setState] = useState({ loading: true });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setState({ loading: true });
    try {
      const { data } = await api.get("/integrations/make-webhook/status");
      setState({ loading: false, ...data });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  };

  useEffect(() => { load(); }, []);

  const fireTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/make-webhook/test");
      setTestResult({ ok: true, delivered: data.delivered });
    } catch (e) {
      setTestResult({ ok: false, error: formatApiError(e) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border border-[var(--line)] bg-white" data-testid="make-webhook-card">
      <div className="px-5 py-4 border-b border-[var(--line)] flex items-center gap-3">
        <Zap size={16} className="text-[var(--gold)]" />
        <div className="flex-1">
          <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Outbound Webhook — Make / Zapier / n8n</div>
          <div className="text-xs text-[var(--muted)]">Fire a JSON payload to your automation platform every time a lead or booking is submitted.</div>
        </div>
        {state.loading ? <Loader2 className="animate-spin" size={14} /> : state.configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 uppercase tracking-widest">
            <CheckCircle2 size={12} /> Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 uppercase tracking-widest">
            <AlertCircle size={12} /> Not Configured
          </span>
        )}
      </div>

      {!state.loading && (
        <div className="p-5 space-y-4">
          {!state.configured && (
            <div className="text-xs text-[var(--muted)] bg-[var(--cream)] border border-[var(--line)] p-3">
              Set one or more of these environment variables on your backend and redeploy to enable webhook delivery:
              <ul className="mt-2 pl-4 list-disc leading-relaxed">
                <li><code>MAKE_WEBHOOK_URL</code> — catch-all URL for all events</li>
                <li><code>MAKE_WEBHOOK_URL_LEADS</code> — only leads / contact-form submissions</li>
                <li><code>MAKE_WEBHOOK_URL_BOOKINGS</code> — only consultation booking requests</li>
              </ul>
              <p className="mt-2">Get the URL from Make → your scenario → "Custom webhook" → "Copy address to clipboard".</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-[var(--line)] p-3">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Leads / Contact Form</div>
              <div className="mt-1 flex items-center gap-2">
                {state.leads_ready ? (
                  <><CheckCircle2 size={12} className="text-green-700" /> <span className="text-green-800 font-bold">READY</span></>
                ) : (
                  <><AlertCircle size={12} className="text-red-700" /> <span className="text-red-800 font-bold">NOT SET</span></>
                )}
              </div>
              {state.leads_url && (
                <div className="mt-1 text-[11px] font-mono text-[var(--muted)] truncate" title={state.leads_url}>{state.leads_url}</div>
              )}
              <div className="mt-2 text-[10px] tracking-widest uppercase font-bold text-[var(--muted)]">Event: lead.created</div>
            </div>

            <div className="border border-[var(--line)] p-3">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[var(--navy)]">Consultation Bookings</div>
              <div className="mt-1 flex items-center gap-2">
                {state.bookings_ready ? (
                  <><CheckCircle2 size={12} className="text-green-700" /> <span className="text-green-800 font-bold">READY</span></>
                ) : (
                  <><AlertCircle size={12} className="text-red-700" /> <span className="text-red-800 font-bold">NOT SET</span></>
                )}
              </div>
              {state.bookings_url && (
                <div className="mt-1 text-[11px] font-mono text-[var(--muted)] truncate" title={state.bookings_url}>{state.bookings_url}</div>
              )}
              <div className="mt-2 text-[10px] tracking-widest uppercase font-bold text-[var(--muted)]">Event: booking.requested</div>
            </div>
          </div>

          {state.configured && (
            <div>
              <button
                onClick={fireTest}
                disabled={testing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--navy)] text-white text-xs font-bold uppercase tracking-widest disabled:opacity-50 hover:bg-[var(--navy-dark,#0c2035)]"
                data-testid="make-webhook-test-btn"
              >
                {testing ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
                Send test payload
              </button>
              {testResult && (
                <div className="mt-2 text-xs">
                  {testResult.ok ? (
                    <div className="text-green-800 bg-green-50 border border-green-300 px-3 py-2" data-testid="make-webhook-test-result">
                      Fired test payloads · lead.created: <strong>{testResult.delivered?.["lead.created"] ? "delivered" : "failed"}</strong> · booking.requested: <strong>{testResult.delivered?.["booking.requested"] ? "delivered" : "failed"}</strong>
                    </div>
                  ) : (
                    <div className="text-red-800 bg-red-50 border border-red-300 px-3 py-2">{testResult.error}</div>
                  )}
                </div>
              )}
            </div>
          )}

          <details className="text-xs text-[var(--muted)]">
            <summary className="cursor-pointer text-[var(--navy)] font-bold text-[11px] tracking-widest uppercase">Payload shape</summary>
            <pre className="mt-2 text-[11px] bg-[var(--navy)] text-neutral-100 p-3 overflow-x-auto whitespace-pre font-mono leading-relaxed">{`{
  "event": "lead.created",
  "at": "2026-09-08T19:40:53.123456+00:00",
  "source": "catalystbhconsulting.com",
  "data": {
    "id": "…",
    "name": "Jane Smith",
    "email": "jane@example.com",
    "organization": "Acme BH",
    "phone": "+15551234567",
    "interest": "Compliance",
    "message": "…",
    "source": "main",
    "status": "lead",
    "created_at": "2026-09-08T19:40:53.123456+00:00"
  }
}`}</pre>
            <p className="mt-2">Booking events use the same shape with <code>"event": "booking.requested"</code> and the booking's <code>slot_date</code>, <code>slot_time</code>, <code>topic</code>, and <code>notes</code>.</p>
          </details>
        </div>
      )}
    </div>
  );
}
